<template>
  <div :class="styleClasses.card">
    <div :class="styleClasses.card_header">
      <span class="me-2">{{ formatTimestamp(reservation.datetime || reservation.created_at, 'HH:mm') }}</span>
      <span>
        {{
          reservation.foreign_type === 21
            ? reservation.course?.label
            : reservation.route === 3
              ? '乗車'
              : `${reservation.user?.username} 様` || '--'
        }}
      </span>
    </div>
    <div :class="styleClasses.card_body">
      <div :class="styleClasses.card_body_content">
        <div>
          <div>
            <!-- メモ -->
            <div
              class="mb-1"
              v-if="reservation.memo">{{ reservation.memo.memo }}</div>

            <!-- 乗降車地点 -->
            <div :class="styleClasses.locations">
              <div
                :class="styleClasses.location"
                @click="mapWindowOpen('pickup')">
                <div>
                  <i class="fa-regular fa-map-location-dot"/>
                  <span class="mt-1">乗</span>
                </div>
                <div>
                  {{ reservation.p_address?.replace('日本、', '') }}<br>{{ reservation.p_title }}
                  <ApproximateWarning v-if="reservation.p_is_approximate === 1"/>
                </div>
              </div>
              <!-- 経由地点 -->
              <div
                :class="styleClasses.location"
                @click="mapWindowOpen('waypoint', i)"
                v-for="(waypoint, i) in reservation.waypoints" :key="waypoint.id">
                <div>
                  <i class="fa-regular fa-map-location-dot"/>
                  <span class="mt-1">経由 {{ (i + 1) }}</span>
                </div>
                <div>{{ waypoint.address?.replace('日本、', '') }}<br>{{ waypoint.title }}</div>
              </div>
              <!-- 降車地点 -->
              <div
                v-if="reservation.d_address || reservation.d_title"
                :class="styleClasses.location"
                @click="mapWindowOpen('destination')">
                <div>
                  <i class="fa-regular fa-map-location-dot"/>
                  <span class="mt-1">降</span>
                </div>
                <div>
                  {{ reservation.d_address?.replace('日本、', '') }}<br>{{ reservation.d_title }}
                  <ApproximateWarning v-if="reservation.d_is_approximate === 1"/>
                </div>
              </div>
            </div>

            <!-- 降車地点の登録がない場合はdriverから登録も可能 -->
            <div
              v-if="!(reservation.d_address || reservation.d_title)"
              :class="styleClasses.location"
              class="mt-2">
              <div>
                <i class="fa-regular fa-map-location-dot"/>
                <span class="mt-1">降</span>
              </div>
              <div class="d-flex align-items-center">
                <div v-if="!destinationSearchResult.lat" class="d-flex align-items-center flex-1">
                  <input
                    type="text" class="form-control" placeholder="桐生市〇〇町××丁目1234"
                    v-model="inputDestinationAddress">
                  <button
                    :disabled="!inputDestinationAddress"
                    @click="searchLocationFromAddress"
                    :class="[styleClasses.button, styleClasses.blue]" class="ms-1">位置取得</button>
                </div>
                <div v-else class="flex-1">
                  <div>
                    <div>{{ destinationSearchResult.address }}</div>
                    <div>{{ destinationSearchResult.title }}</div>
                  </div>
                  <div class="d-flex align-items-center justify-content-end my-1">
                    <button
                      @click="destinationSearchResult = {}"
                      :class="[styleClasses.button, styleClasses.gray]">キャンセル</button>
                    <button
                      @click="calcFare(reservation)"
                      :class="[styleClasses.button, styleClasses.blue]"
                      class="ms-1">登録する</button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="user.role.role === 13"
              class="mt-2">
              <div :class="styleClasses.price">
                <div :class="styleClasses.label">乗車料金</div>
                <div :class="styleClasses.value">
                  <div :class="styleClasses.num">
                    <div>{{ reservation.order?.orderResult?.price || reservation.e_price ? `${addComma(reservation.order?.orderResult?.price || reservation.e_price)} 円` : '未定' }}</div>
                    <div v-if="recalclatePrice"><span>{{ Math.max((reservation.e_price || 0), recalclatePrice).toLocaleString() }}円</span></div>
                  </div>
                  <button :class="[styleClasses.gray, styleClasses.button]" @click="reCalculateFare(reservation.order.id)">再計算</button>
                </div>
              </div>
            </div>

            <button
              v-if="!isCompleted"
              :class="[styleClasses.gray, styleClasses.button]"
              class="mt-2"
              :disabled="reservation.order.flag !== 21"
              @click="mapOpenWithFullRoute(reservation.order.id)">実車ルート案内開始</button>

            <div v-if="reservation.order.flag < 21" class="mt-2">
              <button :class="[styleClasses.red, styleClasses.button]" @click="cancelReservation(reservation.id)">キャンセル</button>
            </div>

            <div class="mt-2" v-if="reservation.route === 1 && reservation.foreign_type === 11">
              <button
                @click="openCustomerOperatorThread"
                :class="[styleClasses.button, styleClasses.gray]">チャットを確認</button>
            </div>

            <div class="mt-2">
              <button
                @click="openOperatorThread"
                :class="[styleClasses.button, styleClasses.gray]">オペレーターに連絡</button>
            </div>
          </div>

          <div v-if="isCompleted" class="mt-2 card">
            <div class="bg-gray bdr-b px-2 py-1">配車履歴</div>
            <div class="px-2 py-1">
              <table>
                <tr v-for="revision in (reservation.order?.revisions || [])" :key="revision.id">
                  <td class="pe-2">{{ formatTimestamp(revision.created_at, 'HH:mm') }}</td>
                  <td class="pe-2">{{ getStatus(revision.to_flag) }}</td>
                  <td>{{ revision.user?.username }}</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
        <div :class="styleClasses.status_btns" v-if="!isCompleted">
          <LargeBtn
            v-for="(btn, i) in statusBtns"
            :key="i"
            :status-flag="btn.flag"
            :office-id="officeId"
            :disabled="!btn.activeFlags.includes(reservation.order.flag)"
            @click="orderUpdateWrapper(btn.flag)">{{ btn.label }}</LargeBtn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import {
  LargeBtn,
  ApproximateWarning,
} from '@/components/parts';
import cf from '@/mixins/commonFunctions.js';

export default {
  name: 'order  ',
  mixins: [cf],
  components: {
    LargeBtn,
    ApproximateWarning,
  },
  props: {
    reservation: Object,
    styleClasses: Object,
  },
  data() {
    return {
      flag: {
        showSlider: true,
      },
      recalclatePrice: 0,
      inputDestinationAddress: '',
      destinationSearchResult: {},
      statusBtns: [
        {
          flag: 2,
          label: '迎車',
          activeFlags: [1],
          color: 'red',
        },
        {
          flag: 11,
          label: '到着',
          activeFlags: [2],
          color: 'blue',
        },
        {
          flag: 21,
          label: '実車',
          activeFlags: [11],
          color: 'yellow',
        },
        {
          flag: 31,
          label: '完了',
          activeFlags: [21],
          color: 'lightgreen',
        },
      ],
      formItems: [
        {
          label: 'メーター',
          type: 'tel',
          value: '',
        },
        {
          label: '乗車人数',
          type: 'tel',
          value: '',
        },
        {
          label: '金額',
          type: 'tel',
          value: '',
        },
      ],
      message: '',
      unreadChatMessages: {},
    };
  },
  computed: {
    ...mapState(['user', 'helper', 'modal']),
    officeId() {
      return this.user?.offices?.[0]?.id || null;
    },
    isCompleted() {
      return this.reservation.order.flag >= 31;
    },
  },
  watch: {
    'modal.contents.updateData': {
      handler(val) {
        if (val.data?.type === 'orderResultUpdate') {
          this.$store.dispatch('user/update');
        }
      },
      deep: true,
    },
  },
  mounted() {},
  methods: {
    getStatus(flag) {
      if (flag === 1) return '配車完了';
      return this.statusBtns.find((status) => status.flag === flag)?.label || '不明';
    },
    /** ローディング表示 */
    showLoading() {
      const args = { modalName: 'modalLoadingBallScaleRippleMultiple' };
      this.$store.dispatch('modal/loadings/showModal', args, { root: true });
    },

    /** ローディング終了 */
    hideLoading() {
      this.$store.dispatch('modal/loadings/hideModal', null, { root: true });
    },

    /** 住所からLatLngを取得 */
    searchLocationFromAddress() {
      this.showLoading();
      this.axios({
        method: 'GET',
        url: '/v1/helper/get/latlng',
        params: { address: this.inputDestinationAddress },
      })
        .then((response) => {
          const res = response.data;
          if (res.isValid) {
            this.destinationSearchResult = {
              title: res.name,
              address: res.address,
              lat: res.latitude,
              lng: res.longitude,
            };
            this.inputDestinationAddress = '';
          } else {
            alert('位置が取得できませんでした。再度お試しください。');
          }
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        })
        .finally(() => {
          this.hideLoading();
        });
    },

    /** ２地点間の距離を取得 */
    async getDistanceDuration({
      pLat,
      pLng,
      dLat,
      dLng,
    }) {
      let distance = null;
      await this.axios({
        method: 'POST',
        url: '/v1/helper/get/distanceDuration',
        params: {
          p_lat: pLat,
          p_lng: pLng,
          d_lat: dLat,
          d_lng: dLng,
          noGenerateMap: true,
        },
      })
        .then((response) => {
          const res = response.data;
          if (res.distance) distance = res.distance;
          else alert('距離計算中にエラーが発生しました。再度お試しください。');
        })
        .catch((error) => {
          console.log(error);
          alert('距離計算中にエラーが発生しました。再度お試しください。');
        });
      return distance;
    },

    async calculateFare(params) {
      let fare = null;
      await this.axios({
        method: 'POST',
        url: '/v1/helper/get/calcTaxiFare',
        data: params,
      })
        .then((response) => {
          const res = response.data;
          if (res.fare) fare = res.fare;
          else alert('エラーが発生しました。再度お試しください。');
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        });
      return fare;
    },

    async calcFare(reservation) {
      this.showLoading();
      // 乗降車地点間の距離を取得
      const distance = await this.getDistanceDuration({
        pLat: reservation.p_lat,
        pLng: reservation.p_lng,
        dLat: this.destinationSearchResult.lat,
        dLng: this.destinationSearchResult.lng,
      });
      // 深夜割り増しの有無判定
      const isMidNight = new Date().getHours() >= 22 || new Date().getHours() < 5;

      if (distance) {
        const fare = await this.calculateFare({
          includePickupFee: false, // ライドシェアなので迎車は考慮しない
          pickupDistance: 0,
          allDistance: distance,
          isMidNight,
          isRideshare: true,
        });
        if (fare) {
          this.registDestination(reservation, fare);
        } else {
          alert('運賃計算中にエラーが発生しました。再度お試しください。');
          this.hideLoading();
        }
      } else {
        alert('乗降車地点間の距離の取得に失敗しました。再度お試しください。');
        this.hideLoading();
      }
    },

    registDestination(reservation, fare) {
      const data = { id: reservation.id };
      Object.keys(this.destinationSearchResult).forEach((key) => {
        data[`d_${key}`] = this.destinationSearchResult[key];
      });
      data.e_price = fare;
      this.axios({
        method: 'POST',
        url: '/v1/reservation/update',
        data,
      })
        .then(() => {
          this.$store.dispatch('user/update');
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        })
        .finally(() => {
          this.hideLoading();
        });
    },

    mapWindowOpen(type, waypointIdx) {
      const targetReservation = this.reservation;
      if (!targetReservation) return;
      let position = '';
      switch (type) {
        case 'destination': position = `${targetReservation.d_lat},${targetReservation.d_lng}`; break;
        case 'waypoint': position = `${targetReservation.waypoints[waypointIdx].lat},${targetReservation.waypoints[waypointIdx].lng}`; break;
        default: position = `${targetReservation.p_lat},${targetReservation.p_lng}`; break;
      }
      if (!position) return;
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${position}&travelmode=driving`, '_blank');
    },

    mapOpenWithFullRoute() {
      const rsv = this.reservation;
      if (!rsv) return;
      const locations = rsv.waypoints.map((w) => `${w.lat},${w.lng}`);
      if (rsv.d_lat && rsv.d_lng) locations.push(`${rsv.d_lat},${rsv.d_lng}`);
      let waypointStr = '';
      let destinationStr = '';
      locations.forEach((l, i) => {
        if (i < locations.length - 1) waypointStr += i === 0 ? l : `|${l}`;
        else destinationStr += l;
      });
      window.open(`https://www.google.com/maps/dir/?api=1&waypoints=${waypointStr}&destination=${destinationStr}&travelmode=driving`, '_blank');
    },

    orderUpdateWrapper(flag) {
      if (!flag) return;
      this.updateOrder(flag);
    },

    updateOrder(flag) {
      this.showLoading();

      const data = { id: this.reservation.order.id, flag, revisionUserId: this.user.id };
      this.axios({
        method: 'POST',
        url: '/v1/order/update',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (!res.updated) alert('予約ステータスの更新に失敗しました。最新の状態に更新します。');
          else if (flag === 31) this.openOrderResultModal();
          // reservation.codeを入れてreplace(ステータス操作したorderに戻ってくるため)
          this.$router.replace({ path: '/order', query: { code: this.reservation.code } });
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        })
        .finally(() => {
          this.$store.dispatch('dispatch/getReservations', { hideLoading: true });
          // ネイティブアプリのバッジ更新（敢えて完了時に絞らない）
          cf.updateNativeAppBadges(this.helper);
        });
    },

    openOrderResultModal() {
      const price = this.reservation?.e_price || null;
      const capacity = this.reservation.order?.vehicle?.capacity || null;

      const args = {
        modalName: 'OrderResult',
        modalTitle: this.user.role.role === 11 ? '配車結果' : '配車完了',
        data: {
          showOrderResultForm: false,
          // 日報入力させたい場合にはこっち↓にする
          // showOrderResultForm: this.user.role.role === 11,
          showThanksMessage: true,
          orderId: this.reservation.order.id,
          price: this.user.role.role === 13 ? price : null,
          capacity,
        },
      };
      this.$store.dispatch('modal/contents/showModal', args, { root: true });
    },

    reCalculateFare() {
      this.showLoading();
      this.axios({
        method: 'GET',
        url: '/v1/order/get/reCalculateFare',
        params: {
          order_id: this.reservation.order.id,
        },
      })
        .then((response) => {
          const res = response.data;
          if (res.fare) {
            this.recalclatePrice = res.fare;
          } else {
            alert('エラーが発生しました。再度お試しください。');
          }
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        })
        .finally(() => {
          this.hideLoading();
        });
    },

    cancelReservation(reservationId) {
      if (!confirm('予約をキャンセルします。\nこの操作は取り消せません。\nよろしいですか？')) return;
      this.showLoading();
      const data = {
        reservation_id: reservationId,
        user_id: this.user.id,
        type: 4,
      };

      this.axios({
        method: 'POST',
        url: '/v1/reservation/cancel',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (res.message === 'Already canceled') {
            alert('既に予約はキャンセルされています。');
          } else if (res.message === 'This reservation is working or completed') {
            alert('選択された予約は、実車中または完了しているため、キャンセルできません。');
          } else if (!res.canceled) {
            alert('予約のキャンセルに失敗しました。');
          }
        })
        .catch((error) => {
          alert('処理中にエラーが発生しました。');
          if (error.response) console.log(error.response.data);
        })
        .finally(() => {
          this.$store.dispatch('dispatch/getReservations', { hideLoading: true });
          // 表示するスライドを調整
          const idx = this.user.driverOrders.findIndex((o) => o.reservation.id === reservationId);
          this.sliderInitIdx = idx > 0 ? idx - 1 : 0;
          this.flag.showSlider = false;
          this.$nextTick(() => { this.flag.showSlider = true; });
        });
    },
    onSlideChange(swiper) {
      this.activeIndex = swiper.activeIndex; // activeIndexを更新
    },
    sendChatToCustomer() {
      if (!this.message) return;
      this.showLoading();
      const data = {
        reservation_id: this.order.reservation.id,
        to_user_id: this.order.reservation.user.id,
        from_user_id: this.user.id,
        message: this.message,
      };
      this.axios({
        method: 'POST',
        url: '/v1/message/create',
        data,
      })
        .then((response) => {
          const res = response.data;
          if (res.sended) {
            alert('メッセージを送信しました。');
            this.message = '';
          } else alert('メッセージの送信に失敗しました。');
        })
        .catch((error) => {
          console.log(error);
          alert('エラーが発生しました。再度お試しください。');
        })
        .finally(() => {
          this.hideLoading();
          this.$store.dispatch('user/update');
        });
    },

    openMessageTemplateModal() {
      const args = {
        modalName: 'MessageTemplate',
        modalTitle: 'テンプレートメッセージ',
      };
      this.$store.dispatch('modal/contents/showModal', args, { root: true });
    },

    openCustomerOperatorThread() {
      const customer = this.reservation.user;
      const userIds = [this.user.id, customer.id];
      this.$store.dispatch('modal/contents/showModal', {
        modalName: 'ThreadMessages',
        modalTitle: `${customer.username}様, オペレーター`,
        modalWidth: '95%',
        data: { userIds, officeId: this.user.offices[0]?.id },
      });
    },
    openOperatorThread() {
      const userIds = [this.user.id];
      this.$store.dispatch('modal/contents/showModal', {
        modalName: 'ThreadMessages',
        modalTitle: 'オペレーター',
        modalWidth: '95%',
        data: { userIds, officeId: this.user.offices[0]?.id },
      });
    },
  },
};
</script>
